/**
 * The leader host's `open()` for a Turso database, and the flattened method
 * surface it exposes over RPC.
 *
 * Runs INSIDE the leader worker only — it is imported by
 * `turso-leader-worker-entry.web.ts`, which is bundled to a public asset by
 * `bun build` and never enters Metro's graph (it transitively imports
 * `loadSyncWasm`, which Metro cannot represent; see `load-sync-wasm.web.ts`).
 *
 * WHY THE RETRY LADDER STAYS HERE and not in `lib/leader-resource`:
 * `openGuestTursoConnection` already carries exactly the right bounded retry —
 * `pRetry`, 6 attempts x 250ms, retrying ONLY `"Access Handles cannot be
 * created"` and wrapping anything else in `AbortError` so a genuine corruption
 * is not masked by ~1.5s of silent retrying. That signature is
 * resource-specific: the leader-resource package is engine-agnostic and cannot
 * know it. The package's contribution is the COARSER layer — if this `open()`
 * ultimately rejects, it surrenders the election lock so a DIFFERENT tab can try
 * (§2.5), which is the layer PGlite lacks entirely.
 *
 * WHAT MOVED IN HERE (and why it must be exactly one instance, process-wide):
 *   - `loadSyncWasm()` + `new Database(...)` — owns the exclusive OPFS handle.
 *   - `createActorSyncConnectionLock()` — it serializes statements on ONE engine
 *     connection. A per-tab lock would serialize nothing across tabs and would
 *     let Tab A's `begin` interleave with Tab B's statements, which is the exact
 *     "cannot start a transaction within a transaction" failure
 *     `actor-sync-connection-lock.ts` exists to prevent.
 *   - `adaptTursoToExpoSqlite` + `withSqliteBusyRetry` — their transaction
 *     framing and `TransactionState.depth` track the CONNECTION's nesting, which
 *     is now shared across tabs, so they must live next to the connection.
 */
import { openGuestTursoConnection } from "#open-guest-turso-connection";
import { openActorTursoSyncConnection } from "#open-actor-turso-sync-connection";
import type {
  ActorTursoSyncConnection,
  ActorTursoSyncOptions,
} from "#open-actor-turso-sync-connection-types";
import {
  buildPullChangePayloads,
  buildStatementChangePayloads,
  TABLE_CHANGED_EVENT_KIND,
  type TableChangedPayload,
} from "#turso-change-event";
import type { LeaderResourceEvent } from "@-/dev.improve.haniverse.lib.leader-resource";
import { splitSqlStatements } from "@-/com.npmjs.expo-sqlite";
import { classifyTransactionControl } from "#actor-sync-connection-lock";
import { createLeaderCallQueue } from "#create-leader-call-queue";
import { createLeaderWriteLedger, LEADER_WRITE_RETENTION_MS } from "#leader-write-ledger";
import type { ActorSchemaUpdate } from "#update-actor-schema";
import {
  getLeaderCallControl,
  isLeaderMaintenanceSql,
  isLeaderReadOnlySql,
  parseLeaderTrackedCall,
  type LeaderTrackedCall,
} from "#leader-tracked-call";

/**
 * The RPC allowlist. Not reflection — a method absent from this list can never be
 * reached from a tab.
 *
 * `prepareAsync` is deliberately ABSENT: it returns a statement OBJECT with
 * methods, and a `postMessage` boundary can only carry data. It is flattened
 * tab-side into `prepareValuesAsync` + the plain read methods; see
 * `open-proxied-turso-connection.web.ts`.
 *
 * `pull` / `push` are the SYNC operations, and they are on this list for the
 * reason the whole leader design exists: Turso's `capture_data_changes` is
 * PER-CONNECTION, so a `push()` only ships the CDC log of the connection it runs
 * on. A tab that pushed through its own engine would ship an empty log while
 * every other tab's writes sat unpushed on the leader's connection. Routing them
 * through the leader means `push()` runs on the SAME connection every tab writes
 * through — the invariant `turso-adapter.ts` states, now enforced across tabs
 * instead of merely within one.
 *
 * Their return values were verified cloneable against sync-common 0.7.2:
 * `pull(): Promise<boolean>`, `push(): Promise<void>`. No projection needed.
 */
export const TURSO_LEADER_METHODS = [
  "executeTracked",
  "runAsync",
  "getAllAsync",
  "getFirstAsync",
  "execAsync",
  "prepareValuesAsync",
  "pull",
  "push",
  "stats",
  "updateSchema",
  "readBackupSnapshot",
  "executeBackupRestore",
  "setBackupRecoveryHold",
  // ── Engine lifecycle, for the sign-in wipe (§5.3) ──────────────────────────
  // These close and reopen the ENGINE while the leader KEEPS the election lock,
  // so no other tab can slip in and re-open the file between the two. See
  // `pauseEngine`/`resumeEngine` on the resource below.
  "closeEngineHoldingLeadership",
  "reopenEngine",
] as const;

/**
 * Shared by the leader host and every client. The client needs the resume
 * allowlist too: `reopenEngine` must reach the SAME leader while ordinary calls
 * are queued behind the deliberate pause.
 */
export const TURSO_LEADER_PAUSE_CONTROL = {
  pauseMethods: ["closeEngineHoldingLeadership"],
  resumeMethods: ["reopenEngine"],
} as const;

export type TursoLeaderMethod = (typeof TURSO_LEADER_METHODS)[number];

/**
 * How long a call may wait at the leader for the shared connection. The longest
 * thing that can legitimately be ahead of it is one sync push/pull; a call still
 * waiting after a minute is behind something stuck, and failing it (it never runs
 * afterwards) beats parking every tab behind it.
 */
const TURSO_CALL_WAIT_DEADLINE_MS = 60_000;

/**
 * Local SQLite work (statements, drizzle migrations rewriting a table) on the
 * OPFS engine. Generous for a local op because an overrun is not a mere
 * timeout: it poisons the connection and forces a leader change.
 */
const TURSO_STATEMENT_RUN_DEADLINE_MS = 60_000;

/**
 * How long each method may RUN on the connection. Sync and restore work is
 * network- or volume-bound, so it gets minutes: a push after a guest replay
 * uploads the whole replayed database, and `reopenEngine` re-runs the actor
 * open (a ~36s connect ladder) plus its bootstrap pull.
 */
const TURSO_LEADER_RUN_DEADLINES_MS: Readonly<Partial<Record<TursoLeaderMethod, number>>> = {
  closeEngineHoldingLeadership: 30_000,
  executeBackupRestore: 180_000,
  pull: 120_000,
  push: 180_000,
  readBackupSnapshot: 90_000,
  reopenEngine: 120_000,
  setBackupRecoveryHold: 30_000,
  // Waits behind a running push on the same connection lock, then reads sync metadata.
  stats: 180_000,
};

function getTursoLeaderRunDeadlineMs(method: string): number {
  const perMethod: unknown = Reflect.get(TURSO_LEADER_RUN_DEADLINES_MS, method);
  return typeof perMethod === "number" ? perMethod : TURSO_STATEMENT_RUN_DEADLINE_MS;
}

/**
 * How long a tab's transaction or pause window may hold the connection with none
 * of its calls running. A drizzle transaction issues its statements back to back
 * and the sign-in pause does a few local file operations; 30s idle means the tab
 * that reserved it is gone or wedged.
 */
const TURSO_RESERVATION_IDLE_DEADLINE_MS = 30_000;

/**
 * The leader's end-to-end bound per method (wait in line + run), advertised to
 * every tab so each waits slightly longer than the leader does.
 */
function buildTursoLeaderRequestDeadlinesMs(): Record<string, number> {
  return Object.fromEntries(
    TURSO_LEADER_METHODS.map((method) => [
      method,
      TURSO_CALL_WAIT_DEADLINE_MS + getTursoLeaderRunDeadlineMs(method),
    ]),
  );
}

/**
 * A `type` alias, NOT an `interface`, and that distinction is load-bearing:
 * TypeScript gives a type alias an implicit index signature but withholds one
 * from an interface, so an interface here would not satisfy `LeaderResourceApi`
 * and the generic would have to be widened to `any` at every call site.
 */
export type TursoLeaderApi = {
  /**
   * Close the engine but KEEP the election lock (§5.3). The sign-in wipe needs
   * the OPFS handle released — `wipeTursoDatabaseFiles` cannot `removeEntry` a
   * file with a live sync access handle — while no other tab is allowed to elect
   * itself and re-open the file in the gap. Holding the lock is what makes that
   * window exclusive.
   *
   * Idempotent: closing an already-closed engine is a no-op, so a retried
   * sign-in cannot double-close.
   */
  closeEngineHoldingLeadership(): Promise<void>;
  execAsync(source: string): Promise<void>;
  executeBackupRestore(
    statements: import("#actor-backup-reader").ActorBackupRestoreStatement[],
  ): Promise<void>;
  executeTracked(
    call: LeaderTrackedCall,
  ): Promise<{ outcome: "applied"; result: unknown } | { outcome: "rolled-back" }>;
  getAllAsync(source: string, params: readonly unknown[]): Promise<unknown[]>;
  getFirstAsync(source: string, params: readonly unknown[]): Promise<unknown>;
  /**
   * drizzle's `.values()` path. Returns POSITIONAL row tuples.
   *
   * The `Object.values` projection stays LEADER-side on purpose: it depends on
   * Turso's key-insertion-order contract (`Statement.readRow` writes columns
   * 0..n in order), which is a property of the engine, not of the transport.
   * Projecting tab-side would move that dependency away from the thing it
   * depends on.
   */
  prepareValuesAsync(source: string, params: readonly unknown[]): Promise<unknown[][]>;
  /**
   * Pull remote changes. Runs under the SAME connection lock as every statement
   * (see `openActorTursoSyncConnection`), so it can never interleave with a
   * drizzle `begin` … `commit`.
   */
  pull(): Promise<boolean>;
  /**
   * Push local CDC changes. MUST run on the leader's connection: CDC is
   * per-connection, so this is the only connection that HAS the changes.
   */
  push(): Promise<void>;
  readBackupSnapshot(tables: readonly string[]): Promise<Record<string, unknown[]>>;
  /**
   * Re-run the resource's `open()` (with its full retry ladder), keeping the
   * election lock throughout. The counterpart to
   * `closeEngineHoldingLeadership`.
   */
  reopenEngine(): Promise<void>;
  runAsync(
    source: string,
    params: readonly unknown[],
  ): Promise<{ changes: number; lastInsertRowId: number }>;
  setBackupRecoveryHold(enabled: boolean): Promise<void>;
  /** The leader connection's count of unsent CDC changes, for the same reason as `push`. */
  stats(): Promise<import("#open-actor-turso-sync-connection-types").ActorTursoSyncStats>;
  /**
   * Bring the database up to the calling tab's migrations (see `update-actor-schema.ts`). The
   * leader already applied its own at open; this is for a tab that carries a newer build.
   */
  updateSchema(migrations: Record<string, string>): Promise<ActorSchemaUpdate>;
};

interface TursoStatementLike {
  executeForRawResultAsync(params?: unknown): Promise<{ getAllAsync(): Promise<unknown[]> }>;
  finalizeAsync(): Promise<void>;
}

/**
 * The subset of the adapter's surface this host actually calls. Deliberately NOT
 * `extends SQLiteDatabase`: the adapter satisfies the expo-sqlite surface only
 * structurally at the call sites the codebase uses (its `prepareAsync` returns
 * its own statement shape, not expo's `SQLiteStatement` with native handles), so
 * extending would demand fields that have never existed on it.
 */
interface TursoConnectionLike {
  execAsync(source: string): Promise<void>;
  getAllAsync(source: string, ...rest: unknown[]): Promise<unknown[]>;
  getFirstAsync(source: string, ...rest: unknown[]): Promise<unknown>;
  prepareAsync(source: string): Promise<TursoStatementLike>;
  runAsync(
    source: string,
    ...rest: unknown[]
  ): Promise<{ changes: number; lastInsertRowId: number }>;
}

const TURSO_CONNECTION_METHOD_LIST = [
  "execAsync",
  "getAllAsync",
  "getFirstAsync",
  "prepareAsync",
  "runAsync",
] as const;

function isTursoConnectionLike(value: unknown): value is TursoConnectionLike {
  return (
    typeof value === "object" &&
    value !== null &&
    TURSO_CONNECTION_METHOD_LIST.every(
      (methodName) => typeof Reflect.get(value, methodName) === "function",
    )
  );
}

function requireTursoConnectionLike(value: unknown): TursoConnectionLike {
  if (!isTursoConnectionLike(value)) {
    throw new TypeError("Turso adapter does not implement the leader connection surface");
  }
  return value;
}

function isUnknownArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

export interface TursoLeaderResource {
  api: TursoLeaderApi;
  /** Serialize calls and keep transaction/pause ownership and change origins scoped to a tab. */
  call(
    method: string,
    args: readonly unknown[],
    originTabId: string,
    invoke?: () => Promise<unknown>,
  ): Promise<unknown>;
  close(): Promise<void>;
  /** The host's handler for a poisoned connection; see `LeaderResourceHostOptions.open`. */
  onUnusable(handler: (reason: string) => void): void;
  /** Per-method end-to-end deadlines the leader enforces; forwarded to every tab. */
  requestDeadlinesMs: Readonly<Record<string, number>>;
  /**
   * Wired to the host's `subscribeEvents`. The leader emits one `table-changed`
   * per committed mutation and one per table after a `pull()` that
   * applied rows; the host broadcasts each to every attached tab.
   */
  subscribeEvents(emit: (event: LeaderResourceEvent) => void): () => void;
  tabClosed(tabId: string): Promise<void>;
}

/**
 * One opened engine: the expo-sqlite-shaped connection plus the sync operations,
 * if the database has any.
 *
 * `sync` is `null` for `guest.db` BY CONSTRUCTION, not by omission — the guest
 * database is never handed a url (see `open-guest-turso-connection.web.ts`
 * rule 1), so it has nothing to pull from or push to. A `pull`/`push` RPC against
 * it therefore throws rather than silently resolving, which would otherwise let a
 * "sync succeeded" be reported for a database that can never sync.
 */
/** What the resource hands every (re)open of its engine. */
interface OpenTursoEngineHooks {
  /**
   * The engine's own connection lock was poisoned: an operation outlived its
   * deadline and may still be running. The resource stops using the engine and
   * has the host end the term so a fresh leader replaces it.
   */
  onDamaged(error: unknown): void;
}

interface OpenedTursoEngine {
  close(): Promise<void>;
  connection: TursoConnectionLike;
  sync: Pick<ActorTursoSyncConnection["tursoDb"], "pull" | "push" | "stats"> | null;
  /** Bring the database up to the given migrations (see `update-actor-schema.ts`). */
  updateSchema(migrations: Readonly<Record<string, string>>): Promise<ActorSchemaUpdate>;
}

/**
 * Build a leader resource around an `openEngine` that can be re-run.
 *
 * The engine lives in a MUTABLE slot rather than being captured once, because
 * §5.3's sign-in wipe closes and reopens it while leadership is retained. Every
 * API method reads the slot at call time, so a statement issued after a reopen
 * lands on the NEW engine, and one issued while paused fails loudly instead of
 * being silently applied to a closed handle.
 *
 * Verified against `@tursodatabase/sync-wasm` 0.7.2: the adapter's `prepareAsync`
 * never calls `tursoDb.prepare()` — it closes over the SQL string and re-issues
 * through `tursoDb.run/all/get` on execute, and the engine's own
 * `Database.run/all/get` are each `prepare → execute → close` in a `finally`.
 * So NO engine-side statement state survives between calls, which is what makes
 * the tab-side flattening in `open-proxied-turso-connection.web.ts`
 * behaviour-preserving rather than merely plausible.
 */
/**
 * SQL that lists the user tables a `pull()` could possibly have touched.
 *
 * Read from `sqlite_master` rather than from `actorSchema`, deliberately. The
 * alternative — importing the drizzle schema — would drag drizzle and all
 * fourteen `_tables.*` packages into the `bun build` leader-worker bundle purely
 * to recover a list of names, and would couple the engine package to the app's
 * schema. `sqlite_master` is also the more honest source: it names what the file
 * ACTUALLY contains, which is exactly the set a pull can write into.
 *
 * ── THE EXCLUSIONS ARE NOT COSMETIC ───────────────────────────────────────────
 * `sqlite_%` drops SQLite's own internal tables. The two `turso` patterns drop
 * sync-wasm's CDC and bookkeeping tables, which come in BOTH spellings — a live
 * two-tab run against a real sync server produced
 * `__turso_internal_seq___turso_internal_autoincrement_turso_cdc`, i.e. a
 * DOUBLE-UNDERSCORE prefix that a `turso_%` pattern alone does not match. An
 * escaped `\_` is required here or the LIKE wildcard `_` would match any single
 * character and silently over-exclude a legitimate table (e.g. `xturso_note`).
 *
 * Leaking one of these is not harmless: every pull would emit a change event for
 * an engine-internal table, waking every table listener in every tab on every
 * pull batch of the drain loop — a cross-tab invalidation storm on the sync hot
 * path, for a table no app query can ever read.
 */
const PULL_TABLE_NAMES_SQL = `select name from sqlite_master
   where type = 'table'
     and name not like 'sqlite\\_%' escape '\\'
     and name not like 'turso\\_%' escape '\\'
     and name not like '\\_\\_turso\\_%' escape '\\'`;

function readTableNameRows(rows: readonly unknown[]): string[] {
  const names: string[] = [];
  for (const row of rows) {
    if (typeof row !== "object" || row === null) {
      continue;
    }
    const name: unknown = Reflect.get(row, "name");
    if (typeof name === "string" && name !== "__haniverse_applied_writes") {
      names.push(name);
    }
  }
  return names;
}

export async function createTursoLeaderResource(
  databaseFilePath: string,
  openEngine: (hooks: OpenTursoEngineHooks) => Promise<OpenedTursoEngine>,
): Promise<TursoLeaderResource> {
  // ── Unusable-connection reporting ──────────────────────────────────────────
  // Two things can leave the engine unusable mid-term: a call that overran its
  // deadline in the call queue below, and the engine's OWN connection lock
  // poisoning itself (a holder outlived its hold deadline — `onDamaged`). Either
  // way work may still be running on the engine, so no further call may start on
  // it: every queued and later call fails at once, and the host is told to end
  // the term, which closes this engine (bounded) and lets a fresh leader open a
  // replacement. Remembered in case it fires before the host registers.
  let unusableReason: string | null = null;
  let onUnusable: ((reason: string) => void) | null = null;
  let failQueuedCalls: ((cause: unknown) => void) | null = null;
  function reportUnusable(cause: unknown): void {
    if (unusableReason !== null) {
      return;
    }
    unusableReason = cause instanceof Error ? cause.message : String(cause);
    failQueuedCalls?.(cause);
    onUnusable?.(unusableReason);
  }
  const engineHooks: OpenTursoEngineHooks = { onDamaged: reportUnusable };

  let engine: OpenedTursoEngine | null = await openEngine(engineHooks);

  // ── Cross-tab change fan-out (§4.6) ────────────────────────────────────────
  // `null` until the host subscribes, which it does once per leadership term.
  let emitEvent: ((event: LeaderResourceEvent) => void) | null = null;
  // Safe across awaits: the connection queue executes exactly one RPC at a time.
  let currentOriginTabId: string | null = null;
  let transactionChanges: TableChangedPayload[] | null = null;
  let activeTransactionId: string | null = null;
  const ledger = createLeaderWriteLedger(() => requireEngine().connection);
  // A synced replica receives the receipts table from its cloud copy (the server owns it), so the
  // leader never runs DDL on a replica. A local database has no one else to create it.
  const initializeLedger = (opened: OpenedTursoEngine) =>
    ledger.initialize({ createsTable: opened.sync === null });
  function statementChangePayloads(sql: string, isScript: boolean): TableChangedPayload[] {
    return buildStatementChangePayloads({
      databaseFilePath,
      isScript,
      originTabId: currentOriginTabId,
      sql,
    });
  }

  function emitPayloads(payloads: readonly TableChangedPayload[]): void {
    const emit = emitEvent;
    if (emit === null) {
      return;
    }
    for (const payload of payloads) {
      emit({ kind: TABLE_CHANGED_EVENT_KIND, payload });
    }
  }

  /**
   * Emit one `table-changed` per mutating statement, AFTER it completed.
   *
   * After, not before, and that ordering is the same one
   * `attach-sqlite-change-feed.ts` chose for the same reason: a change event that
   * outran its own write would let a listener re-read the database and observe the
   * PRE-write state, which is precisely the stale-read window the deterministic
   * feed exists to close.
   */
  function emitStatementChange(sql: string, isScript: boolean): void {
    if (emitEvent === null) {
      return;
    }
    const payloads = statementChangePayloads(sql, isScript);
    if (transactionChanges !== null) {
      transactionChanges.push(...payloads);
    } else {
      emitPayloads(payloads);
    }
  }

  /**
   * Table names for the pull fan-out, cached per ENGINE instance.
   *
   * Cleared on `closeEngineHoldingLeadership`, because the sign-in wipe deletes
   * and re-bootstraps the file — a list carried across that window would describe
   * a database that no longer exists.
   */
  let cachedPullTableNames: readonly string[] | null = null;

  async function readPullTableNames(): Promise<readonly string[]> {
    if (cachedPullTableNames !== null) {
      return cachedPullTableNames;
    }
    const rows = await requireEngine().connection.getAllAsync(PULL_TABLE_NAMES_SQL, []);
    cachedPullTableNames = readTableNameRows(rows);
    return cachedPullTableNames;
  }

  /**
   * Fan out a pull. Fire-and-forget on purpose: `pull()`'s RESULT must not depend
   * on the notification succeeding, and awaiting the `sqlite_master` read inside
   * `pull()` would put a query inside the connection lock's slot on the drain
   * loop's hot path. A failure here means one missed refresh, never a failed pull.
   */
  function emitPullChange(): void {
    if (emitEvent === null) {
      return;
    }
    void readPullTableNames()
      .then((tableNames) => {
        emitPayloads(
          buildPullChangePayloads({ appliedChanges: true, databaseFilePath, tableNames }),
        );
      })
      .catch(() => {
        // Best-effort: a tab that missed one pull notification still refetches on
        // its next navigation or its own write. Throwing here would surface a
        // notification failure as a SYNC failure, which it is not.
      });
  }

  /**
   * Read the live engine, or fail loudly.
   *
   * The paused window is deliberately an ERROR rather than a queue: the pause
   * exists so `wipeTursoDatabaseFiles` can delete the file underneath, so a
   * statement that "waited it out" would run against a DIFFERENT database than
   * the caller believed it was addressing. The client puts tabs into a queueing
   * state via `leader-paused` for exactly this reason — anything that still
   * arrives here is a call that raced the broadcast, and failing it is honest.
   */
  function requireEngine(): OpenedTursoEngine {
    if (engine === null) {
      throw new Error("Turso leader engine is closed for a sign-in reset (leader-paused)");
    }
    return engine;
  }

  function requireSync(): NonNullable<OpenedTursoEngine["sync"]> {
    const { sync } = requireEngine();
    if (sync === null) {
      throw new TypeError(
        "This Turso leader resource has no sync connection (local-only database)",
      );
    }
    return sync;
  }

  const api: TursoLeaderApi = {
    closeEngineHoldingLeadership: async () => {
      // Idempotent — a retried sign-in must not double-close.
      const current = engine;
      if (current === null) {
        return;
      }
      // Null the slot BEFORE awaiting the close: an `await` here is a yield
      // point, and a statement arriving during it must not reach an engine that
      // is already tearing down.
      engine = null;
      // The wipe deletes and re-bootstraps the file, so a table list read from
      // the OLD engine describes a database that will not exist.
      cachedPullTableNames = null;
      await current.close();
    },
    execAsync: async (source: string) => {
      await requireEngine().connection.execAsync(source);
      // A script: split and sniff each statement, mirroring the local feed.
      emitStatementChange(source, true);
    },
    executeBackupRestore: async (statements) => {
      const connection = requireEngine().connection;
      const method: unknown = Reflect.get(connection, "executeBackupRestore");
      if (typeof method !== "function") {
        throw new TypeError("Isolated restore is unavailable");
      }
      await Reflect.apply(method, connection, [statements]);
      emitPullChange();
    },
    executeTracked: async (input) => {
      const call = parseLeaderTrackedCall(input);
      // Never replay after receipt retention expires (a suspended browser tab
      // can outlive every request timeout). Failure is safer than duplication.
      if (Date.now() - call.createdAt >= LEADER_WRITE_RETENTION_MS) {
        throw new Error("Write recovery window expired; the write was not replayed");
      }
      const source = String(call.args[0]);
      const control = getLeaderCallControl(call.args);
      const invoke = async (args = call.args): Promise<unknown> => {
        const fn = api[call.method];
        return Reflect.apply(fn, api, args);
      };
      if (control === "commit" && call.transactionId !== activeTransactionId) {
        const receipt = await ledger.read(call.transactionId ?? call.writeId);
        return receipt.applied
          ? { outcome: "applied", result: receipt.result }
          : { outcome: "rolled-back" };
      }
      if (control === "begin") {
        await invoke();
        activeTransactionId = call.transactionId ?? call.writeId;
        return { outcome: "applied", result: { changes: 0, lastInsertRowId: 0 } };
      }
      if (control === "commit") {
        // drizzle ignores COMMIT's row metadata. Save its acknowledgement before
        // COMMIT so a lost response can resolve without rerunning the callback.
        const result = call.method === "execAsync" ? undefined : { changes: 0, lastInsertRowId: 0 };
        await ledger.finish(call.transactionId ?? call.writeId, result);
        await invoke();
        activeTransactionId = null;
        await ledger.prune().catch(() => {});
        return { outcome: "applied", result };
      }
      if (control === "rollback") {
        const result = await invoke();
        activeTransactionId = null;
        return { outcome: "applied", result };
      }
      if (call.transactionId !== null) {
        if (call.transactionId !== activeTransactionId) {
          return { outcome: "rolled-back" };
        }
        return { outcome: "applied", result: await invoke() };
      }
      // Maintenance changes connection configuration, not account data, and some
      // PRAGMAs/VACUUM cannot run inside a transaction. Never automatically resend them.
      if (isLeaderReadOnlySql(call.args) || isLeaderMaintenanceSql(call.args)) {
        return { outcome: "applied", result: await invoke() };
      }
      const receipt = await ledger.read(call.writeId);
      if (receipt.applied) {
        return { outcome: "applied", result: receipt.result };
      }
      const statements = splitSqlStatements(source);
      // Migration scripts sometimes carry their own outer BEGIN/COMMIT. The
      // receipt must be inside that unit, never before or after it.
      const framedScript =
        call.method === "execAsync" &&
        statements.length > 1 &&
        classifyTransactionControl(statements[0] ?? "") === "begin" &&
        classifyTransactionControl(statements.at(-1) ?? "") === "commit";
      const bodyStatements = framedScript ? statements.slice(1, -1) : statements;
      if (bodyStatements.some((statement) => classifyTransactionControl(statement) !== null)) {
        // An embedded COMMIT could publish effects before their receipt exists.
        // Reject the whole script before executing anything in that case.
        throw new Error("Tracked SQL scripts must contain one complete transaction");
      }
      const args = framedScript ? [statements.slice(1, -1).join("; ")] : call.args;
      await requireEngine().connection.execAsync("BEGIN");
      transactionChanges = [];
      try {
        const result = await invoke(args);
        await ledger.finish(call.writeId, result);
        await requireEngine().connection.execAsync("COMMIT");
        const changes = transactionChanges;
        transactionChanges = null;
        emitPayloads(changes);
        // Pruning must never turn an already committed write into a failure.
        await ledger.prune().catch(() => {});
        return { outcome: "applied", result };
      } catch (error) {
        transactionChanges = null;
        await requireEngine().connection.execAsync("ROLLBACK");
        throw error;
      }
    },
    getAllAsync: async (source: string, params: readonly unknown[]) => {
      const rows = await requireEngine().connection.getAllAsync(source, [...params]);
      // Sniffed even though it is nominally a read: drizzle runs
      // `INSERT … RETURNING` through `getAllAsync`, so the METHOD never decides
      // whether something mutated — only the SQL does.
      emitStatementChange(source, false);
      return rows;
    },
    getFirstAsync: async (source: string, params: readonly unknown[]) => {
      const row = await requireEngine().connection.getFirstAsync(source, [...params]);
      emitStatementChange(source, false);
      return row;
    },
    prepareValuesAsync: async (source: string, params: readonly unknown[]) => {
      const statement = await requireEngine().connection.prepareAsync(source);
      try {
        const raw = await statement.executeForRawResultAsync([...params]);
        const rows = await raw.getAllAsync();
        if (!rows.every(isUnknownArray)) {
          throw new TypeError(
            "Turso adapter returned a non-positional row from prepareValuesAsync",
          );
        }
        // drizzle's `.values()` path also carries `INSERT … RETURNING`.
        emitStatementChange(source, false);
        return rows;
      } finally {
        // A legitimate no-op today (the adapter documents that Turso's
        // `run/all/get` finalize their own statements), kept so the shape stays
        // honest if the adapter ever acquires real statement state.
        await statement.finalizeAsync();
      }
    },
    pull: async () => {
      const appliedChanges = await requireSync().pull();
      // THE SECOND SOURCE OF CHANGE (§4.6). A pull applies remote rows to the
      // SHARED engine below the statement layer — no hook fires, and no tab's
      // local feed hears anything, not even the leader's own. So the leader fans
      // out one event per table itself. Without this, only the leader tab's
      // `use-actor-turso-remote-sync` would refresh (it synthesizes the same
      // events locally) and every proxy tab would stay stale.
      if (appliedChanges) {
        emitPullChange();
      }
      return appliedChanges;
    },
    push: async () => requireSync().push(),
    readBackupSnapshot: async (tables) => {
      const connection = requireEngine().connection;
      const { hasActorBackupSnapshotReader } = await import("#actor-backup-reader");
      if (!hasActorBackupSnapshotReader(connection)) {
        throw new Error("Database does not support consistent backup reads");
      }
      return connection.readBackupSnapshot(tables);
    },
    reopenEngine: async () => {
      if (engine !== null) {
        // Already open — the sign-in path may retry, and re-opening a live engine
        // would strand the current one holding the OPFS handle.
        return;
      }
      cachedPullTableNames = null;
      engine = await openEngine(engineHooks);
      activeTransactionId = null;
      try {
        await initializeLedger(engine);
      } catch (error) {
        const failed = engine;
        engine = null;
        await failed.close();
        throw error;
      }
    },
    runAsync: async (source: string, params: readonly unknown[]) => {
      const result = await requireEngine().connection.runAsync(source, [...params]);
      emitStatementChange(source, false);
      return result;
    },
    setBackupRecoveryHold: async (enabled) => {
      const method: unknown = Reflect.get(requireEngine().connection, "setBackupRecoveryHold");
      if (typeof method !== "function") {
        throw new TypeError("Recovery hold is unavailable");
      }
      await Reflect.apply(method, requireEngine().connection, [enabled]);
    },
    stats: async () => requireSync().stats(),
    updateSchema: async (migrations) => {
      const update = await requireEngine().updateSchema(migrations);
      if (update.applied.length > 0) {
        // New tables and columns appeared under every tab's queries.
        emitPullChange();
      }
      return update;
    },
  };

  const calls = createLeaderCallQueue(
    async () => {
      transactionChanges = null;
      activeTransactionId = null;
      if (engine === null) {
        // A tab can also die while holding the image/wipe pause window.
        await api.reopenEngine();
      } else {
        await engine.connection.execAsync("ROLLBACK");
      }
    },
    {
      cleanupDeadlineMs: getTursoLeaderRunDeadlineMs("reopenEngine"),
      // A call overran and may still be running on the engine: replace it.
      onPoisoned: reportUnusable,
      reservationIdleDeadlineMs: TURSO_RESERVATION_IDLE_DEADLINE_MS,
      runDeadlineMs: TURSO_STATEMENT_RUN_DEADLINE_MS,
      waitDeadlineMs: TURSO_CALL_WAIT_DEADLINE_MS,
    },
  );
  failQueuedCalls = (cause) => {
    calls.close(cause);
  };
  if (unusableReason !== null) {
    // Damaged while the engine was still opening: never serve a call on it.
    calls.close(new Error(unusableReason));
  }
  try {
    await initializeLedger(engine);
  } catch (error) {
    await engine.close();
    engine = null;
    throw error;
  }

  return {
    api,
    call: (method, args, originTabId, invoke) => {
      const sqlArgs = method === "executeTracked" ? parseLeaderTrackedCall(args[0]).args : args;
      const control = getLeaderCallControl(sqlArgs);
      return calls.run(
        originTabId,
        control === "begin" || method === "closeEngineHoldingLeadership",
        control === "commit" || control === "rollback" || method === "reopenEngine",
        async () => {
          currentOriginTabId = originTabId;
          try {
            const fn: unknown = Reflect.get(api, method);
            if (typeof fn !== "function") {
              throw new TypeError("Unknown Turso leader method");
            }
            const result: unknown = invoke === undefined ? await fn(...args) : await invoke();
            if (control === "begin") {
              transactionChanges = [];
            }
            if (control === "commit") {
              const changes = transactionChanges;
              transactionChanges = null;
              if (changes !== null) {
                emitPayloads(changes);
              }
            }
            if (control === "rollback") {
              transactionChanges = null;
            }
            return result;
          } finally {
            currentOriginTabId = null;
          }
        },
        // Even the owning tab's sync must wait for its transaction to end:
        // sync takes the same engine lock that COMMIT needs to release.
        method === "push" || method === "pull" || method === "stats" || method === "updateSchema",
        { operation: `Turso leader ${method}`, runDeadlineMs: getTursoLeaderRunDeadlineMs(method) },
      );
    },
    close: async () => {
      calls.close(new Error("Turso leader closed"));
      const current = engine;
      engine = null;
      cachedPullTableNames = null;
      await current?.close();
    },
    onUnusable: (handler) => {
      onUnusable = handler;
      if (unusableReason !== null) {
        handler(unusableReason);
      }
    },
    requestDeadlinesMs: buildTursoLeaderRequestDeadlinesMs(),
    subscribeEvents: (emit: (event: LeaderResourceEvent) => void) => {
      emitEvent = emit;
      return () => {
        emitEvent = null;
      };
    },
    tabClosed: calls.tabClosed,
  };
}

/** Build the leader `open()` for the signed-out (guest) database. */
export async function openGuestTursoLeaderResource(
  databaseName: string,
): Promise<TursoLeaderResource> {
  // `databaseName` doubles as the change feed's `databaseFilePath`, matching what
  // the page passes to `attachSqliteChangeFeed` for the same database — the two
  // must agree or a cross-tab event would not match the local feed's shape.
  return createTursoLeaderResource(databaseName, async () => {
    // Carries the bounded access-handle retry ladder; see this file's header.
    const guest = await openGuestTursoConnection(databaseName);
    return {
      close: async () => {
        // Fold the WAL back into the main database file BEFORE closing.
        //
        // Guest writes checkpoint inside the adapter lock before resolving (see
        // `open-guest-turso-connection.web.ts`). Keep this final best-effort checkpoint too: it
        // covers shutdown after any engine-level write that bypassed the statement classifier.
        //
        // Best-effort on purpose: a checkpoint can legitimately fail if another connection still
        // holds a read lock, and a failure here must not stop the close below from releasing the
        // OPFS access handle — a leaked handle is the worse outcome (see
        // `opfs-access-handle-lock-conflict-web`).
        try {
          await requireTursoConnectionLike(guest.connection).execAsync(
            "PRAGMA wal_checkpoint(TRUNCATE)",
          );
        } catch {
          /* best-effort: the close below still releases the access handle. */
        }
        await guest.close();
      },
      connection: requireTursoConnectionLike(guest.connection),
      // Local-only by construction: `guest.db` is never handed a url. See
      // `OpenedTursoEngine.sync`.
      sync: null,
      updateSchema: guest.updateSchema,
    };
  });
}

/**
 * Build the leader `open()` for the signed-in (sync) database.
 *
 * Delegates to `openActorTursoSyncConnection`, deliberately rather
 * than reimplementing its ladder here. That function encodes two separately
 * debugged production failures and every part of it is load-bearing:
 *
 *   - `releaseAbandonedDatabase()` reclaims the OPFS handle of an attempt whose
 *     caller already timed out — the `web-url-routing` staging failure.
 *   - `wipeTursoDatabaseFiles` on retries of newly created files, because re-connecting to a
 *     PARTIALLY-written sync file yields a connection that looks healthy and
 *     syncs in neither direction.
 *   - `pTimeout` (12s) per `connect()`, so a transport hang cannot block the open
 *     forever.
 *   - `pRetry` (3 attempts), which the leader-resource host then backs with its
 *     own COARSER layer: an `open()` that ultimately rejects surrenders the
 *     election lock so a DIFFERENT tab can try.
 *
 * Running inside the worker changes none of that. `markConnectionDelivered()` is
 * called immediately because nothing can abandon this open and then reuse the
 * engine: the leader host does bound `open()` (it holds the election lock), but
 * on expiry it closes whatever a late open produces and shuts the whole worker
 * down, which releases the OPFS handle — the connection is never orphaned AND
 * left running the way the page-side open could leave it.
 */
export async function openActorTursoLeaderResource(
  options: ActorTursoSyncOptions,
): Promise<TursoLeaderResource> {
  return createTursoLeaderResource(options.databaseName, async ({ onDamaged }) => {
    // On web nothing else replaces a damaged connection: the lock fails every later statement
    // fast, and this leader ends its term so the next open (here or in another tab) replaces it.
    const sync = await openActorTursoSyncConnection({ ...options, onDamaged });
    // Delivery IS taken here: unlike the page-side caller, nothing above this
    // frame can abandon the open, so leaving it tracked as reclaimable would let
    // a LATER open close the connection this leader is actively serving.
    sync.markConnectionDelivered?.();
    return {
      close: async () => {
        await sync.tursoDb.close();
      },
      connection: requireTursoConnectionLike(sync.connection),
      // The SAME connection every statement above runs on, and its `pull`/`push`
      // are already wrapped in that connection's lock by the opener. This is the
      // CDC invariant made cross-tab: one connection, one CDC log, one pusher.
      sync: sync.tursoDb,
      updateSchema: (migrations) => {
        if (sync.updateSchema === undefined) {
          throw new Error("This sync connection cannot update its schema on request");
        }
        return sync.updateSchema(migrations);
      },
    };
  });
}

/**
 * Defence-in-depth precondition, re-checked INSIDE the worker.
 *
 * The page already runs `checkTursoWebSupport()` before mounting the database
 * layer, and that stays in the page (an unsupported browser should never pay for
 * a worker). This is the worker-side re-check: a worker in a NON-isolated context
 * fails deep inside `loadSyncWasm` with "SharedArrayBuffer transfer requires
 * self.crossOriginIsolated", which is a far worse error to surface than a clean
 * precondition failure.
 */
export async function checkLeaderWorkerPrecondition(): Promise<
  { ok: true } | { ok: false; reason: string }
> {
  if (typeof SharedArrayBuffer === "undefined") {
    return { ok: false, reason: "SharedArrayBuffer is unavailable in this worker" };
  }
  if (globalThis.self !== undefined && globalThis.crossOriginIsolated === false) {
    return {
      ok: false,
      reason: "worker is not cross-origin isolated (COOP/COEP headers missing)",
    };
  }
  return { ok: true };
}
